import React, { useRef, useCallback, useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { backendConfig, joinBackendPath, toSameOriginMediaUrl } from '../config/backendConfig';
import { Loader2, X } from 'lucide-react';
import './ChooseArtworkModal.css';

// Map artworkTypeKey (slug) to Firestore typeOfArtwork (label). Must match AdminArtworks + LearnArtHistory.
const KEY_TO_LABEL = {
  'portrait': 'Portrait',
  'landscape': 'Landscape',
  'still-life': 'Still Life',
  'abstract': 'Abstract',
  'sacred': 'Sacred',
  'genre-scene': 'Genre Scene',
  'self-portrait': 'Self-Portrait',
  'architectural': 'Architectural',
  'animal-birds': 'Animal/Birds',
  'fantasy': 'Fantasy',
  'study-sketch': 'Study/Sketch',
  'miniature': 'Miniature',
  'calligraphy': 'Calligraphy',
  'sculpture': 'Sculpture',
  'interior': 'Interior',
  'seascape': 'Seascape',
  'floral': 'Floral',
  'objects': 'Objects',
};

export function getTypeOfArtworkFromKey(artworkTypeKey) {
  return KEY_TO_LABEL[artworkTypeKey] || (artworkTypeKey || '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Reverse map: Firestore typeOfArtwork label -> slug key. Used when resuming a flow whose card has no saved category. */
const LABEL_TO_KEY = {
  ...Object.fromEntries(Object.entries(KEY_TO_LABEL).map(([k, v]) => [v, k])),
  // Alias older labels to current categories
  'Flower painting': 'floral',
  'Flower Painting': 'floral',
  'Animal': 'animal-birds',
  'Animals': 'animal-birds',
  'Animal / Birds': 'animal-birds',
};
// Masters live in GCS (typically `*-verified-artworks`); Firestore only stores `verifiedImageStoragePath` etc.
// Do not construct `firebasestorage.../v0/b/<default-app-bucket>/o/verified-artworks/...` — files may only exist in the dedicated bucket; use `/api/verified-artwork-file?path=` (backend signs the correct bucket).
const VERIFIED_STORAGE_PREFIX = 'verified-artworks/';
const VERIFIED_PREVIEW_PREFIX = 'verified-artworks-previews/';
const CARD_LEVEL_OPTIONS = ['Level 1', 'Level 2', 'Level 3', 'Unspecified'];
const CARD_ARTWORK_TYPES = Object.values(KEY_TO_LABEL);
const CARD_ART_MOVEMENTS = [
  'Romanticism', 'Impressionism', 'Expressionism', 'Realism', 'Surrealism',
  'Abstract Expressionism', 'Baroque', 'Contemporary', 'Abstract Art', 'Post-Impressionism',
  'Art Nouveau', 'Conceptual Art', 'Minimalism', 'Pop Art', 'Academic Art',
  'Symbolism', 'Synthetism', 'Art Informel', 'Cubism', 'Post-Painterly Abstraction', 'Neoclassicism',
  'Pointillism', 'Fauvism', 'Neo-Suprematism',
  'Rococo', 'Neo-Expressionism', 'Op Art', 'Post-Minimalism', 'Dutch Golden Age',
  'Concrete Art (Concretism)', 'Modernism', 'Socialist Realism', 'Contemporary Realism',
  'Mannerism (Late Renaissance)', 'Northern Renaissance', 'English Renaissance', 'Naive Art', 'Feminist Art', 'Social Realism',
  'Magic Realism', 'Neo-Minimalism (Neo-Geo)', 'Constructivism', 'Kitsch', 'High Renaissance',
  'Early Renaissance', 'Orientalism', 'Avant-garde', 'Tachisme', 'Tenebrism', 'Neo-Dada',
  'Photorealism (Super-Realism, Hyper-Realism)', 'Street art', 'Naturalism', 'Modernismo', 'Light and Space',
  'Futurism', 'Kinetic art', 'Environmental Art (Land art)', 'Dada', 'Neo-Pop Art', 'Pre-Raphaelite movement', 'Company School (or Company Style) of painting', 'Rajput Painting / Uniara School', 'Neo-Impressionism',
  'Art Deco', 'Performance Art', 'Arte Povera', 'Regionalism', 'Muralism', 'Gothic Art', 'Tonalism',
  'De Stijl (Neoplasticism)', 'Precisionism', 'Arts and Crafts', 'Suprematism', 'Digital Art', 'Byzantine Art', 'Islamic art', 'Safavid period',
  'Ukiyo-e', 'Ink and wash painting'
];
const MODAL_ARTWORK_CACHE = new Map(); // key -> artworks[]
const MODAL_PICKER_SESSION_PREFIX = 'artmosphere:modalPicker';
/** Payload shape for sessionStorage list snapshots */
const MODAL_PICKER_SESSION_PAYLOAD_V = 2;

function modalPickerSessionStorageKey(cacheKey) {
  return `${MODAL_PICKER_SESSION_PREFIX}:${MODAL_LIST_CACHE_VERSION}:${cacheKey}`;
}

/** Round-trip Firestore Timestamps and Dates so JSON.parse preserves behavior after reload. */
function serializePickerRowForSession(value, depth = 0) {
  if (depth > 24) return null;
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === 'bigint') return { __amBigInt: true, v: value.toString() };
  if (value instanceof Timestamp) {
    return { __amTs: true, s: value.seconds, n: value.nanoseconds };
  }
  if (value instanceof Date) {
    return { __amDate: true, iso: value.toISOString() };
  }
  if (Array.isArray(value)) {
    return value.map((x) => serializePickerRowForSession(x, depth + 1));
  }
  if (value && typeof value === 'object') {
    const out = {};
    Object.keys(value).forEach((k) => {
      const v = value[k];
      if (v !== undefined) out[k] = serializePickerRowForSession(v, depth + 1);
    });
    return out;
  }
  return value;
}

function deserializePickerRowFromSession(value, depth = 0) {
  if (depth > 24) return value;
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    return value.map((x) => deserializePickerRowFromSession(x, depth + 1));
  }
  if (value && typeof value === 'object') {
    if (value.__amTs === true && typeof value.s === 'number') {
      return new Timestamp(value.s, Number(value.n) || 0);
    }
    if (value.__amDate === true && typeof value.iso === 'string') {
      return new Date(value.iso);
    }
    if (value.__amBigInt === true && typeof value.v === 'string') {
      return value.v;
    }
    const out = {};
    Object.keys(value).forEach((k) => {
      out[k] = deserializePickerRowFromSession(value[k], depth + 1);
    });
    return out;
  }
  return value;
}

function readModalPickerListSession(cacheKey) {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(modalPickerSessionStorageKey(cacheKey));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || data.v !== MODAL_PICKER_SESSION_PAYLOAD_V || !Array.isArray(data.rows)) return null;
    return data.rows.map((row) => deserializePickerRowFromSession(row));
  } catch (_) {
    return null;
  }
}

function writeModalPickerListSession(cacheKey, rows) {
  if (typeof sessionStorage === 'undefined' || !cacheKey || !Array.isArray(rows)) return;
  try {
    const payload = {
      v: MODAL_PICKER_SESSION_PAYLOAD_V,
      rows: rows.map((r) => serializePickerRowForSession(r)),
    };
    sessionStorage.setItem(modalPickerSessionStorageKey(cacheKey), JSON.stringify(payload));
  } catch (e) {
    if (e?.name === 'QuotaExceededError' && typeof console !== 'undefined' && console.warn) {
      // eslint-disable-next-line no-console
      console.warn('[ChooseArtworkModal] sessionStorage full; picker list not persisted until reload');
    }
  }
}

const MODAL_PICKER_LOCAL_PREFIX = 'artmosphere:modalPickerLs';
/** Skip localStorage write when JSON exceeds this (~2.4MB chars) to reduce quota failures */
const MAX_MODAL_PICKER_LOCAL_BYTES = 2_400_000;

function modalPickerLocalStorageKey(cacheKey) {
  return `${MODAL_PICKER_LOCAL_PREFIX}:${MODAL_LIST_CACHE_VERSION}:${cacheKey}`;
}

function readModalPickerListLocal(cacheKey) {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(modalPickerLocalStorageKey(cacheKey));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || data.v !== MODAL_PICKER_SESSION_PAYLOAD_V || !Array.isArray(data.rows)) return null;
    return data.rows.map((row) => deserializePickerRowFromSession(row));
  } catch (_) {
    return null;
  }
}

function writeModalPickerListLocal(cacheKey, rows) {
  if (typeof localStorage === 'undefined' || !cacheKey || !Array.isArray(rows)) return;
  try {
    const payload = {
      v: MODAL_PICKER_SESSION_PAYLOAD_V,
      rows: rows.map((r) => serializePickerRowForSession(r)),
    };
    const json = JSON.stringify(payload);
    if (json.length > MAX_MODAL_PICKER_LOCAL_BYTES) {
      try {
        localStorage.removeItem(modalPickerLocalStorageKey(cacheKey));
      } catch (_) {
        /* ignore */
      }
      if (typeof console !== 'undefined' && console.warn) {
        // eslint-disable-next-line no-console
        console.warn(
          '[ChooseArtworkModal] picker list too large for localStorage; using session + network only'
        );
      }
      return;
    }
    localStorage.setItem(modalPickerLocalStorageKey(cacheKey), json);
  } catch (e) {
    if (e?.name === 'QuotaExceededError' && typeof console !== 'undefined' && console.warn) {
      // eslint-disable-next-line no-console
      console.warn('[ChooseArtworkModal] localStorage quota; durable picker cache skipped');
    }
  }
}

/** Tab session + survives browser restart (when under size cap). */
function persistModalPickerListStores(cacheKey, rows) {
  writeModalPickerListSession(cacheKey, rows);
  writeModalPickerListLocal(cacheKey, rows);
}
/** Rows get high fetch priority so first viewport + overscan tiles compete fairly vs browser default (~6 conn/host). */
const MODAL_HIGH_PRIORITY_TILES = 24;
/** URLs pumped through `new Image()` before `<img>` mounts — widen so strip scroll stays cache-warm. */
const INITIAL_PICKER_PRELOAD_TILES = 36;
/** `<link rel="preload" as="image">` hints for first paint (browser deprioritizes dup in-flight requests). */
const HEAD_LINK_PRELOAD_PREVIEW_TILES = 8;
const HEAD_LINK_PRELOAD_HIGH_PRIORITY_TILES = 4;
/**
 * Indices beyond the visible strip window to warm on scroll (Image() → HTTP cache before `<img>` mounts).
 * Larger = fewer beige tiles while scrolling; balances bandwidth.
 */
const SCROLL_PREFETCH_AHEAD = 16;
/** Virtualized horizontal picker: fixed tile geometry (must match Tailwind `w-[260px]` + `gap-4`). */
const PICKER_CARD_WIDTH_PX = 260;
const PICKER_GAP_PX = 16;
const PICKER_CARD_STEP_PX = PICKER_CARD_WIDTH_PX + PICKER_GAP_PX;
/** Tiles rendered past each edge of the viewport so flick-scroll rarely hits empty space */
const PICKER_VIRTUAL_OVERSCAN = 6;
const MOBILE_PICKER_MQ = '(max-width: 767px)';
function pickerStripTotalWidth(count) {
  if (count <= 0) return 0;
  return count * PICKER_CARD_WIDTH_PX + Math.max(0, count - 1) * PICKER_GAP_PX;
}

function computeMiddleWidth(start, end, n) {
  const vc = end - start;
  if (vc <= 0) return 0;
  const gapCount = end >= n ? Math.max(0, vc - 1) : vc;
  return vc * PICKER_CARD_WIDTH_PX + gapCount * PICKER_GAP_PX;
}

function computeTrailingWidth(totalWidth, start, end, n) {
  return Math.max(0, totalWidth - start * PICKER_CARD_STEP_PX - computeMiddleWidth(start, end, n));
}

function computePickerVisibleSlice(scrollLeft, viewportW, n, overscan) {
  if (n <= 0 || viewportW <= 0) return { start: 0, end: 0 };
  const first = Math.floor(scrollLeft / PICKER_CARD_STEP_PX);
  const visibleSlots = Math.ceil(viewportW / PICKER_CARD_STEP_PX) + 2;
  const start = Math.max(0, first - overscan);
  let end = Math.min(n, first + visibleSlots + overscan);
  if (end <= start) end = Math.min(n, start + 1);
  return { start, end };
}
/** Bump when image URL / list shape changes — invalidates in-memory map and `sessionStorage` picker snapshots. */
const MODAL_LIST_CACHE_VERSION = 'v36-preview-only';
/** Picker tiles: canonical `/api/artwork-preview/{id}.webp` only (previews generated in backend). */
/** Parallel Image() preloads — align with typical per-host HTTP concurrency (~6–8). */
const PRELOAD_CONCURRENCY = 8;

const DEBUG_ARTWORK_IMAGES =
  process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_ARTWORK_IMAGES === 'true';

/** Picker list rows spread full Firestore docs (`d.data()`) so repaired URLs in any field are available to thumbnail logic. */

function normalizeArtworkType(type = '') {
  const t = String(type || '').trim();
  if (!t) return '';
  if (t === 'Cityscape') return 'Architectural';
  if (t === 'Flower painting' || t === 'Flower Painting') return 'Floral';
  if (t === 'Animal' || t === 'Animals' || t === 'Animal / Birds') return 'Animal/Birds';
  if (t === 'Architectural/Building') return 'Architectural';
  return t;
}

function typeMatchesBucket(currentType, bucketType) {
  if (!bucketType) return false;
  if (currentType === bucketType) return true;
  if (bucketType === 'Floral' && /flower painting/i.test(currentType)) return true;
  if (bucketType === 'Animal/Birds' && /^animal(\s*\/\s*birds)?s?$/i.test(currentType)) return true;
  return false;
}

function isStrictVerifiedBucketRecord(data = {}) {
  const path = typeof data.verifiedImageStoragePath === 'string' ? data.verifiedImageStoragePath : '';
  const strictApproved = data.verifiedStrictCommercialApproved === true;
  const hasVerifiedPath = path.startsWith(VERIFIED_STORAGE_PREFIX);
  return strictApproved && hasVerifiedPath;
}

export function getKeyFromTypeOfArtwork(typeOfArtworkLabel) {
  if (!typeOfArtworkLabel || typeof typeOfArtworkLabel !== 'string') return null;
  const trimmed = typeOfArtworkLabel.trim();
  return LABEL_TO_KEY[trimmed] || trimmed.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

function isDevSameOriginHost() {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname || '';
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    /^192\.168\./.test(h) ||
    /^10\./.test(h) ||
    /^172\.(1[6-9]|2\d|3[0-1])\./.test(h)
  );
}

/** Canonical picker thumbnail: streamed WebP from GCS at verified-artworks-previews/{id}.webp */
export function getArtworkPickerPreviewUrl(artworkId) {
  const id = String(artworkId || '').trim();
  if (!id) return '';
  // Cache-bust stale failed entries in Safari/local dev image cache.
  const devBuster =
    typeof window !== 'undefined' && isDevSameOriginHost()
      ? (() => {
          if (!window.__ARTMO_PREVIEW_CB) {
            window.__ARTMO_PREVIEW_CB = String(Date.now());
          }
          return window.__ARTMO_PREVIEW_CB;
        })()
      : '';
  const q = devBuster ? `?v=2026-05-preview3&cb=${encodeURIComponent(devBuster)}` : '?v=2026-05-preview3';
  const path = `/api/artwork-preview/${encodeURIComponent(id)}.webp${q}`;
  const base = String(backendConfig.baseUrl || '').replace(/\/$/, '');
  if (!base) {
    if (typeof window !== 'undefined') {
      const h = window.location.hostname || '';
      const dev =
        h === 'localhost' ||
        h === '127.0.0.1' ||
        /^192\.168\./.test(h) ||
        /^10\./.test(h) ||
        /^172\.(1[6-9]|2\d|3[0-1])\./.test(h);
      if (dev) return path;
    }
    return '';
  }
  const absolute = joinBackendPath(base, path);
  // Local Node API: use full `http://host:3001/...` for <img> so the browser never treats the
  // request as a CRA static route (some setups return index.html for `/api/...` → broken decode).
  // Staging/prod still use `toSameOriginMediaUrl` so Firebase / Cloud Run URLs behave as before.
  if (backendConfig.type === 'local') {
    return absolute;
  }
  return toSameOriginMediaUrl(absolute);
}

/**
 * Firestore string fields that may hold a repaired or legacy image URL. Order matches
 * `repairMissingVerifiedStoragePath` / Admin primary url fallbacks / pipeline getPrimaryImageUrl.
 * (`verifiedImageCardPreviewUrl` is handled separately before this list.)
 */
const PICKER_REPAIR_URL_FIELD_ORDER = [
  'verifiedImageUrl',
  'imageUrl',
  'verifiedImageUrlRemote',
  'originalImageUrl',
  'coverUrl',
  'artworkImageUrl',
  'originalImageUrlBackup',
  'thumbnailUrl',
  'sourceImageUrl',
  'publicImageUrl',
];

/** `gs://bucket/verified-artworks/...` → storage path for `/api/verified-artwork-file`. */
function gsUriToVerifiedStoragePath(gs = '') {
  const s = String(gs || '').trim();
  if (!s.startsWith('gs://')) return '';
  const m = s.match(/^gs:\/\/[^/]+\/(.+)$/);
  if (!m) return '';
  let path = m[1];
  try {
    path = decodeURIComponent(path);
  } catch (_) {}
  return isAllowedCardPreviewStoragePath(path) ? path : '';
}

/** Nested metadata / admin patches sometimes store URLs only under `metadata`. */
function gatherNestedPickerHttpsUrls(artwork = {}) {
  const urls = [];
  const push = (v) => {
    const s = String(v || '').trim();
    if (/^https?:\/\//i.test(s)) urls.push(s);
  };
  try {
    const m = artwork.metadata;
    if (m && typeof m === 'object') {
      push(m.imageUrl);
      if (m.artworkMeta && typeof m.artworkMeta === 'object') {
        push(m.artworkMeta.imageUrl);
        push(m.artworkMeta.thumbnailUrl);
      }
    }
  } catch (_) {}
  return urls;
}

/**
 * Picker thumbnail chain: **only** canonical WebP tile `/api/artwork-preview/{id}.webp`.
 * (Full-resolution URLs belong to post-selection / detail views — see `getArtworkFullImageCandidates`.)
 */
export function getPickerImageCandidateUrls(artwork = {}, artworkId = '') {
  const id = String(artworkId || artwork.id || '').trim();
  const u = getArtworkPickerPreviewUrl(id);
  return u ? [u] : [];
}

/** Recompute picker URLs after in-memory cache hydration (needs full doc fields, not projection-only). */
function remapPickerArtworkRow(row) {
  if (!row || typeof row !== 'object' || !row.id) return row;
  const previewUrl = getArtworkPickerPreviewUrl(row.id);
  return {
    ...row,
    pickerPreviewUrl: previewUrl,
    pickerImageUrls: previewUrl ? [previewUrl] : [],
  };
}

function isPreviewTileUrl(x) {
  const s = String(x || '').trim();
  return (
    /^https?:\/\//i.test(s) ||
    s.startsWith('/api/') ||
    s.startsWith('/proxy-image') ||
    s.startsWith('/uploads/')
  );
}

function isRenderableMediaUrl(x) {
  const s = String(x || '').trim();
  return (
    /^https?:\/\//i.test(s) ||
    s.startsWith('/api/') ||
    s.startsWith('/proxy-image') ||
    s.startsWith('/uploads/')
  );
}

function proxyImageUrl(url = '', opts = {}) {
  const u = String(url || '').trim();
  if (!/^https?:\/\//i.test(u)) return '';
  if (u.includes('/proxy-image?')) return '';
  const base = String(backendConfig.baseUrl || '').replace(/\/$/, '');
  const w = opts.maxWidth ?? opts.w;
  const wq =
    w !== undefined && w !== null && Number.isFinite(Number(w))
      ? `&w=${Math.min(1024, Math.max(64, Math.round(Number(w))))}`
      : '';
  const qs = `url=${encodeURIComponent(u)}${wq}`;
  if (!base) {
    if (typeof window !== 'undefined' && isDevSameOriginHost()) {
      return toSameOriginMediaUrl(`/proxy-image?${qs}`);
    }
    return '';
  }
  const absolute = `${base}/proxy-image?${qs}`;
  return toSameOriginMediaUrl(absolute);
}

function isAllowedCardPreviewStoragePath(p) {
  const s = String(p || '').trim();
  return (
    s.startsWith(VERIFIED_STORAGE_PREFIX) ||
    s.startsWith(VERIFIED_PREVIEW_PREFIX)
  );
}

function buildCardPreviewUrls(artwork, base) {
  const cardPath =
    typeof artwork.verifiedImageCardPreviewStoragePath === 'string'
      ? artwork.verifiedImageCardPreviewStoragePath.trim()
      : '';
  const cardPreviewApi =
    cardPath && isAllowedCardPreviewStoragePath(cardPath)
      ? joinBackendPath(base, `/api/verified-artwork-file?path=${encodeURIComponent(cardPath)}`)
      : '';
  const cardPreviewRemote = String(artwork.verifiedImageCardPreviewUrl || '').trim();
  return { cardPreviewRemote, cardPreviewApi };
}

/**
 * Full-resolution chain after selection / Step 1 — may include master API and verifiedImageUrl.
 */
export function getArtworkFullImageCandidates(artwork = {}) {
  const base = String(backendConfig.baseUrl || '');
  const verified = String(artwork.verifiedImageUrl || '').trim();
  const storagePath =
    typeof artwork.verifiedImageStoragePath === 'string' ? artwork.verifiedImageStoragePath.trim() : '';
  const masterApi =
    storagePath && storagePath.startsWith(VERIFIED_STORAGE_PREFIX)
      ? joinBackendPath(base, `/api/verified-artwork-file?path=${encodeURIComponent(storagePath)}`)
      : '';

  const { cardPreviewRemote, cardPreviewApi } = buildCardPreviewUrls(artwork, base);

  const ordered = [];
  const push = (u) => {
    const raw = String(u || '').trim();
    if (!raw) return;
    const x = toSameOriginMediaUrl(raw).trim();
    if (!isRenderableMediaUrl(x)) return;
    if (!ordered.includes(x)) ordered.push(x);
  };

  push(verified);
  push(masterApi);
  push(cardPreviewRemote);
  push(cardPreviewApi);

  for (const k of PICKER_REPAIR_URL_FIELD_ORDER) {
    const v = String(artwork[k] || '').trim();
    if (!v) continue;
    if (v.startsWith('gs://')) {
      const p = gsUriToVerifiedStoragePath(v);
      if (p) push(joinBackendPath(base, `/api/verified-artwork-file?path=${encodeURIComponent(p)}`));
      continue;
    }
    if (/^https?:\/\//i.test(v)) {
      push(v);
      const px = proxyImageUrl(v, { w: 420 });
      if (px) push(px);
    } else if (v.startsWith('/uploads/')) {
      push(joinBackendPath(base, v));
    }
  }

  for (const u of gatherNestedPickerHttpsUrls(artwork)) {
    push(u);
    const px = proxyImageUrl(u, { w: 420 });
    if (px) push(px);
  }

  [verified, masterApi].filter(Boolean).forEach((u) => {
    const px = proxyImageUrl(u, { w: 420 });
    if (px) push(px);
  });

  return ordered;
}

/**
 * Modal: "Choose what you want to learn from today" or "Choose artwork to compare"
 * - Horizontal scroll of artworks filtered by artworkTypeKey + level (artHistoryArtworks).
 * - mode 'compare': excludeId (primary artwork) from list; different title/button.
 */
const ChooseArtworkModal = ({
  isOpen,
  onClose,
  artworkTypeKey,
  level,
  artworkTypeLabel,
  onSelectAndStart,
  /** When mode is 'compare', exclude this id (e.g. primary artwork) from the list */
  excludeId = null,
  /** 'start' = begin flow (Start Step 1); 'compare' = choose second artwork to compare */
  mode = 'start',
  /** Override confirm button label; default: Start Step 1 (start) / Compare Artworks (compare) */
  confirmLabel = null,
  /** Optional callback with loaded strict list count for this type+level */
  onLoadedCount = null,
}) => {
  const scrollRef = useRef(null);
  const [pickerMobile, setPickerMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_PICKER_MQ).matches
  );
  /** Avoid re-running scroll-into-view when the same artwork stays selected across list refreshes. */
  const lastAlignedSelectionIdRef = useRef(null);
  /** Furthest artwork index already queued in scroll-driven `runImagePreload` (monotonic forward). */
  const maxScrollPrefetchIdxRef = useRef(0);
  const scrollPrefetchRafRef = useRef(0);
  /** Horizontal virtualization: only `artworks.slice(start, end)` mount (DOM + images). */
  const [visibleSlice, setVisibleSlice] = useState({ start: 0, end: 0 });
  const [artworks, setArtworks] = useState([]);
  /** Latest list for warmup effect without re-running on every `artworks` identity change */
  const artworksRef = useRef(artworks);
  artworksRef.current = artworks;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [savingById, setSavingById] = useState({});
  const [saveErrById, setSaveErrById] = useState({});
  const [deletingById, setDeletingById] = useState({});

  const typeOfArtwork = getTypeOfArtworkFromKey(artworkTypeKey);
  const levelType = level != null ? `Level ${level}` : 'Level 1';
  const isCompare = mode === 'compare';
  const excludeIds = excludeId == null ? [] : (Array.isArray(excludeId) ? excludeId : [excludeId]);
  const cacheKey = useMemo(
    () => `${MODAL_LIST_CACHE_VERSION}|${typeOfArtwork}|${levelType}|${[...excludeIds].sort().join(',')}|${isCompare ? 'compare' : 'start'}`,
    [typeOfArtwork, levelType, excludeIds, isCompare]
  );
  const label = confirmLabel ?? (isCompare ? 'Compare Artworks' : 'Start Step 1');

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(MOBILE_PICKER_MQ);
    const sync = () => setPickerMobile(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const updateVisibleSlice = useCallback(() => {
    const el = scrollRef.current;
    const n = artworks.length;
    if (!n) {
      setVisibleSlice((prev) => (prev.start === 0 && prev.end === 0 ? prev : { start: 0, end: 0 }));
      return;
    }
    if (pickerMobile) {
      setVisibleSlice((prev) => (prev.start === 0 && prev.end === n ? prev : { start: 0, end: n }));
      return;
    }
    const vw =
      el && el.clientWidth > 0
        ? el.clientWidth
        : typeof window !== 'undefined'
          ? Math.min(window.innerWidth - 96, 864)
          : 864;
    const sl = el?.scrollLeft ?? 0;
    const next = computePickerVisibleSlice(sl, vw, n, PICKER_VIRTUAL_OVERSCAN);
    setVisibleSlice((prev) =>
      prev.start === next.start && prev.end === next.end ? prev : next
    );
  }, [artworks.length, pickerMobile]);

  const pickerSlice = useMemo(() => {
    const n = artworks.length;
    if (!n) return { start: 0, end: 0 };
    const { start, end } = visibleSlice;
    if (start === 0 && end === 0 && n > 0) {
      const vw =
        typeof window !== 'undefined'
          ? Math.min(window.innerWidth - 96, 864)
          : 864;
      return computePickerVisibleSlice(0, vw, n, PICKER_VIRTUAL_OVERSCAN);
    }
    return visibleSlice;
  }, [artworks.length, visibleSlice]);

  const pickerStripLayout = useMemo(() => {
    const n = artworks.length;
    if (pickerMobile) {
      return { stripW: null, start: 0, end: n, trailing: 0, leadWidth: 0 };
    }
    const stripW = pickerStripTotalWidth(n);
    const { start, end } = pickerSlice;
    const trailing = computeTrailingWidth(stripW, start, end, n);
    const leadWidth = start * PICKER_CARD_STEP_PX;
    return { stripW, start, end, trailing, leadWidth };
  }, [artworks.length, pickerSlice, pickerMobile]);

  const preloadSignature = useMemo(
    () => artworks.slice(0, 48).map((a) => a.id).join('|'),
    [artworks]
  );

  const runImagePreload = useCallback((from, to) => {
    if (typeof window === 'undefined') return () => {};
    const entries = [];
    for (let i = from; i < to; i += 1) {
      const row = artworks[i];
      const u = String(getArtworkPickerPreviewUrl(row?.id || '')).trim();
      if (!isPreviewTileUrl(u)) continue;
      entries.push({ url: u, tileIndex: i });
    }
    let cursor = 0;
    let inflight = 0;

    const kick = () => {
      while (inflight < PRELOAD_CONCURRENCY && cursor < entries.length) {
        const { url, tileIndex } = entries[cursor];
        cursor += 1;
        inflight += 1;
        const img = new Image();
        if ('fetchPriority' in img && tileIndex < MODAL_HIGH_PRIORITY_TILES) {
          img.fetchPriority = 'high';
        } else if ('fetchPriority' in img) {
          img.fetchPriority = 'auto';
        }
        img.decoding = 'async';
        const done = () => {
          inflight -= 1;
          kick();
        };
        img.onload = done;
        img.onerror = done;
        img.src = url;
      }
    };

    const schedule = window.setTimeout(kick, 0);

    return () => {
      clearTimeout(schedule);
    };
  }, [artworks]);

  /**
   * On every horizontal scroll, prefetch the next batch of preview URLs ahead of the viewport so tiles
   * decode from cache instead of showing empty placeholders (extends monotonically by index).
   */
  useEffect(() => {
    if (!isOpen || !artworks.length || pickerMobile) return undefined;
    const el = scrollRef.current;
    if (!el) return undefined;
    maxScrollPrefetchIdxRef.current = 0;
    const n = artworks.length;

    const flushScrollPrefetch = () => {
      const vw = el.clientWidth || 1;
      const sl = el.scrollLeft || 0;
      const first = Math.floor(sl / PICKER_CARD_STEP_PX);
      const visSlots = Math.ceil(vw / PICKER_CARD_STEP_PX) + 2;
      const windowEnd = Math.min(n, first + visSlots + SCROLL_PREFETCH_AHEAD);
      const start = maxScrollPrefetchIdxRef.current;
      if (start >= windowEnd) return;
      void runImagePreload(start, windowEnd);
      maxScrollPrefetchIdxRef.current = windowEnd;
    };

    const onScroll = () => {
      if (scrollPrefetchRafRef.current) return;
      scrollPrefetchRafRef.current = requestAnimationFrame(() => {
        scrollPrefetchRafRef.current = 0;
        flushScrollPrefetch();
      });
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => flushScrollPrefetch());
      ro.observe(el);
    }
    const raf0 = requestAnimationFrame(flushScrollPrefetch);
    return () => {
      cancelAnimationFrame(raf0);
      if (ro) ro.disconnect();
      el.removeEventListener('scroll', onScroll);
      if (scrollPrefetchRafRef.current) cancelAnimationFrame(scrollPrefetchRafRef.current);
      scrollPrefetchRafRef.current = 0;
    };
  }, [isOpen, cacheKey, artworks.length, preloadSignature, runImagePreload, pickerMobile]);

  useLayoutEffect(() => {
    if (!isOpen || !artworks.length) return;
    updateVisibleSlice();
  }, [isOpen, cacheKey, artworks.length, updateVisibleSlice, pickerMobile]);

  useEffect(() => {
    if (!isOpen || !artworks.length || pickerMobile) return undefined;
    const el = scrollRef.current;
    if (!el) return undefined;
    const onScroll = () => updateVisibleSlice();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [isOpen, cacheKey, artworks.length, updateVisibleSlice, pickerMobile]);

  useEffect(() => {
    if (!isOpen || !artworks.length || pickerMobile) return undefined;
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => updateVisibleSlice());
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen, cacheKey, artworks.length, updateVisibleSlice, pickerMobile]);

  useEffect(() => {
    if (!isOpen) lastAlignedSelectionIdRef.current = null;
  }, [isOpen]);

  /** Prime HTTP cache before paint where possible — thumbnails still wait on network/GCS + TCP limits. */
  useLayoutEffect(() => {
    if (!isOpen || !artworks.length) return undefined;
    return runImagePreload(0, Math.min(INITIAL_PICKER_PRELOAD_TILES, artworks.length));
  }, [isOpen, cacheKey, artworks.length, runImagePreload]);

  /** Hint the loader so first-strip previews compete with CSS/fonts when modal opens. */
  useLayoutEffect(() => {
    if (!isOpen || !artworks.length || typeof document === 'undefined') return undefined;
    const head = document.head;
    const appended = [];
    const n = Math.min(HEAD_LINK_PRELOAD_PREVIEW_TILES, artworks.length);
    for (let i = 0; i < n; i += 1) {
      const u = String(getArtworkPickerPreviewUrl(artworks[i]?.id || '')).trim();
      if (!isPreviewTileUrl(u)) continue;
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = u;
      if (i < HEAD_LINK_PRELOAD_HIGH_PRIORITY_TILES) link.setAttribute('fetchpriority', 'high');
      head.appendChild(link);
      appended.push(link);
    }
    return () => {
      appended.forEach((el) => el.remove());
    };
  }, [isOpen, cacheKey, preloadSignature]);

  /** After a new selection, gently align that card into view without fighting the user on data refreshes. */
  useLayoutEffect(() => {
    if (!isOpen || !selectedArtwork || !artworks.length) return;
    const id = selectedArtwork.id;
    if (lastAlignedSelectionIdRef.current === id) return;
    const el = scrollRef.current;
    if (!el) return;
    const idx = artworks.findIndex((a) => a.id === id);
    if (idx < 0) return;
    lastAlignedSelectionIdRef.current = id;
    if (pickerMobile) {
      const card = el.querySelector(`[data-picker-artwork-id="${CSS.escape(id)}"]`);
      card?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      return;
    }
    const cardLeft = idx * PICKER_CARD_STEP_PX;
    const cardRight = cardLeft + PICKER_CARD_WIDTH_PX;
    const viewL = el.scrollLeft;
    const viewR = el.scrollLeft + el.clientWidth;
    const pad = 12;
    if (cardLeft < viewL + pad) {
      el.scrollTo({ left: Math.max(0, cardLeft - pad), behavior: 'smooth' });
    } else if (cardRight > viewR - pad) {
      el.scrollTo({ left: cardRight - el.clientWidth + pad, behavior: 'smooth' });
    }
  }, [isOpen, selectedArtwork, artworks, pickerMobile]);

  // Fetch artworks: artHistoryArtworks where typeOfArtwork == typeOfArtwork AND levelType == levelType
  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setLoading(false);
      return;
    }
    if (!artworkTypeKey) {
      setArtworks([]);
      setSelectedArtwork(null);
      setLoading(false);
      setError(isCompare
        ? 'Artwork category wasn\'t saved for this flow. Start a new flow from the Art World to choose artworks to compare.'
        : null);
      return;
    }
    let cancelled = false;
    let cached = MODAL_ARTWORK_CACHE.get(cacheKey);
    if (!Array.isArray(cached) || cached.length === 0) {
      const fromSession = readModalPickerListSession(cacheKey);
      if (Array.isArray(fromSession) && fromSession.length > 0) {
        MODAL_ARTWORK_CACHE.set(cacheKey, fromSession);
        cached = fromSession;
      }
    }
    if (!Array.isArray(cached) || cached.length === 0) {
      const fromLocal = readModalPickerListLocal(cacheKey);
      if (Array.isArray(fromLocal) && fromLocal.length > 0) {
        MODAL_ARTWORK_CACHE.set(cacheKey, fromLocal);
        cached = fromLocal;
      }
    }
    if (Array.isArray(cached) && cached.length > 0) {
      setLoading(false);
    } else {
      setLoading(true);
      setArtworks([]);
    }
    setError(null);
    if (!cached || cached.length === 0) setSelectedArtwork(null);

    const run = async () => {
      try {
        const labelsToQuery = typeOfArtwork === 'Floral'
          ? ['Floral', 'Flower painting', 'Flower Painting']
          : typeOfArtwork === 'Animal/Birds'
            ? ['Animal/Birds', 'Animal', 'Animals', 'Animal / Birds']
            : [typeOfArtwork];

        const snapshots = await Promise.all(
          labelsToQuery.map((lab) =>
            getDocs(
              query(
                collection(db, 'artHistoryArtworks'),
                where('typeOfArtwork', '==', lab),
                where('levelType', '==', levelType),
                where('verifiedStrictCommercialApproved', '==', true)
              )
            )
          )
        );

        const seen = new Set();
        let list = [];
        snapshots.forEach((snapshot) => {
          snapshot.docs.forEach((d) => {
            if (!seen.has(d.id)) {
              seen.add(d.id);
              const raw = d.data() || {};
              if (!isStrictVerifiedBucketRecord(raw)) return;
              const previewUrl = getArtworkPickerPreviewUrl(d.id);
              list.push({
                ...raw,
                id: d.id,
                pickerPreviewUrl: previewUrl,
                pickerImageUrls: previewUrl ? [previewUrl] : [],
                fullImageCandidates: getArtworkFullImageCandidates(raw),
              });
            }
          });
        });
        list = list.map((row, idx) => ({ ...row, _order: idx }));

        if (cancelled) return;
        if (excludeIds.length) {
          const set = new Set(excludeIds);
          list = list.filter((a) => !set.has(a.id));
        }
        MODAL_ARTWORK_CACHE.set(cacheKey, list);
        persistModalPickerListStores(cacheKey, list);
        setArtworks(list);
        if (typeof onLoadedCount === 'function') onLoadedCount(list.length);
        if (list.length === 0) setError(isCompare ? 'No other artworks found for this type and level to compare.' : 'No artworks found for this type and level.');

      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Could not load artworks.');
          setArtworks([]);
          if (typeof onLoadedCount === 'function') onLoadedCount(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    (async () => {
      if (Array.isArray(cached) && cached.length > 0 && !cancelled) {
        setArtworks(cached.map(remapPickerArtworkRow));
      }
      await run();
    })();
    return () => { cancelled = true; };
  }, [isOpen, artworkTypeKey, typeOfArtwork, levelType, isCompare, excludeId, cacheKey]);

  const handleConfirm = () => {
    if (!selectedArtwork) return;
    onSelectAndStart(selectedArtwork);
    onClose();
  };

  const updateArtworkMeta = async (art, nextType, nextLevel, nextMovement) => {
    const type = normalizeArtworkType(nextType);
    const levelRaw = String(nextLevel || '').trim();
    const nextLevelTypeValue = levelRaw === 'Unspecified' ? '' : levelRaw;
    const movementProvided = typeof nextMovement !== 'undefined';
    const movement = movementProvided ? String(nextMovement || '').trim() : String(art.artMovement || art.movement || '').trim();
    if (type && !CARD_ARTWORK_TYPES.includes(type)) return;
    setSavingById((prev) => ({ ...prev, [art.id]: true }));
    setSaveErrById((prev) => ({ ...prev, [art.id]: '' }));
    try {
      const patch = {
        levelType: nextLevelTypeValue,
        artMovement: movement,
        movement,
        artworkCategoryReviewNeeded: false,
        artworkTypeReviewNeeded: false,
        artworkTypeTagSource: type ? 'admin_manual' : (art.artworkTypeTagSource || 'admin_manual'),
        artworkTypeReviewedAt: serverTimestamp(),
        artworkTypeReviewedBy: auth.currentUser?.uid || null,
        updatedAt: serverTimestamp(),
      };
      if (type) {
        patch.typeOfArtwork = type;
        patch.artworkType = type;
      }
      await updateDoc(doc(db, 'artHistoryArtworks', art.id), patch);
      const nextLevelDisplay = nextLevelTypeValue || 'Unspecified';
      const bucketLevelDisplay = levelType || 'Unspecified';
      const nextTypeDisplay = type || normalizeArtworkType(art.typeOfArtwork || art.artworkType || '');
      const stillInCurrentBucket =
        typeMatchesBucket(nextTypeDisplay, typeOfArtwork) &&
        nextLevelDisplay === bucketLevelDisplay;

      setArtworks((prev) => {
        if (!stillInCurrentBucket) {
          const next = prev.filter((row) => row.id !== art.id);
          MODAL_ARTWORK_CACHE.set(cacheKey, next);
          persistModalPickerListStores(cacheKey, next);
          return next;
        }
        const next = prev.map((row) =>
          row.id === art.id
            ? {
                ...row,
                ...(type ? { typeOfArtwork: type, artworkType: type } : {}),
                levelType: nextLevelTypeValue,
                artMovement: movement,
                movement,
                artworkCategoryReviewNeeded: false,
                artworkTypeReviewNeeded: false,
                artworkTypeTagSource: type ? 'admin_manual' : (row.artworkTypeTagSource || 'admin_manual'),
              }
            : row
        );
        MODAL_ARTWORK_CACHE.set(cacheKey, next);
        persistModalPickerListStores(cacheKey, next);
        return next;
      });
      setSelectedArtwork((prev) =>
        prev?.id === art.id && stillInCurrentBucket
          ? {
              ...prev,
              ...(type ? { typeOfArtwork: type, artworkType: type } : {}),
              levelType: nextLevelTypeValue,
                artMovement: movement,
                movement,
            }
          : (prev?.id === art.id ? null : prev)
      );
    } catch (e) {
      setSaveErrById((prev) => ({ ...prev, [art.id]: e?.message || 'Save failed' }));
    } finally {
      setSavingById((prev) => ({ ...prev, [art.id]: false }));
    }
  };

  const deleteArtwork = async (art) => {
    if (!art?.id) return;
    if (!window.confirm('Delete this artwork from the catalog? This removes the Firestore record.')) return;
    setDeletingById((prev) => ({ ...prev, [art.id]: true }));
    setSaveErrById((prev) => ({ ...prev, [art.id]: '' }));
    try {
      await deleteDoc(doc(db, 'artHistoryArtworks', art.id));
      setArtworks((prev) => {
        const next = prev.filter((row) => row.id !== art.id);
        MODAL_ARTWORK_CACHE.set(cacheKey, next);
        persistModalPickerListStores(cacheKey, next);
        return next;
      });
      setSelectedArtwork((prev) => (prev?.id === art.id ? null : prev));
    } catch (e) {
      setSaveErrById((prev) => ({ ...prev, [art.id]: e?.message || 'Delete failed' }));
    } finally {
      setDeletingById((prev) => ({ ...prev, [art.id]: false }));
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, [isOpen, handleKeyDown]);

  /** Trackpad / mouse wheel: map vertical wheel to horizontal strip scroll. */
  useEffect(() => {
    if (!isOpen || loading || error || pickerMobile) return undefined;
    const el = scrollRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth + 2) return;
      const dy = e.deltaY;
      const dx = e.deltaX;
      if (Math.abs(dy) <= Math.abs(dx)) return;
      e.preventDefault();
      el.scrollLeft += dy;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [isOpen, loading, error, artworks.length, pickerMobile]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateVisibleSlice();
    const t = requestAnimationFrame(() => updateVisibleSlice());
    return () => cancelAnimationFrame(t);
  }, [isOpen, artworks.length, updateVisibleSlice]);

  if (!isOpen) return null;

  /** Single blocking state: Firestore load or preview warmup — not shown when `error` is set (message replaces UI). */
  const blockingLoader = !error && loading;

  const modalTree = (
    <div
      className={clsx(
        'fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-2 sm:p-4 transition-[backdrop-filter] duration-300',
        /* No grey scrim — blur the live page behind for a premium glass feel */
        blockingLoader
          ? 'bg-transparent backdrop-blur-[36px] backdrop-saturate-150 supports-[backdrop-filter]:bg-transparent'
          : 'backdrop-blur-xl supports-[backdrop-filter]:bg-white/10 bg-white/5'
      )}
      onClick={() => {
        if (!blockingLoader) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="choose-artwork-modal-title"
      aria-describedby="choose-artwork-modal-desc"
    >
      <div
        className="relative flex h-[min(92dvh,860px)] w-full max-w-4xl flex-col overflow-hidden rounded-t-3xl sm:rounded-3xl border-2 border-amber-200 bg-amber-50/95 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {blockingLoader ? (
          <div
            className="absolute inset-0 z-[50] flex flex-col items-center justify-center rounded-[1.4rem] bg-gradient-to-b from-amber-50/97 via-amber-50/95 to-orange-50/92 px-8 py-16 text-center shadow-inner ring-1 ring-amber-100/70 backdrop-blur-[2px]"
            role="status"
            aria-live="polite"
            aria-busy="true"
            aria-label="Loading artworks"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 z-[60] rounded-full p-2 text-amber-900/60 hover:bg-amber-100/80 hover:text-amber-950 focus:outline-none focus:ring-2 focus:ring-amber-400"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <Loader2 className="mb-5 h-12 w-12 shrink-0 text-amber-600 animate-spin" aria-hidden />
            <p className="text-lg font-semibold tracking-tight text-amber-950">Loading artworks…</p>
            <p className="mt-2 max-w-xs text-sm text-amber-900/65">This only takes a moment.</p>
          </div>
        ) : null}

        <div
          className={clsx(
            'flex min-h-0 flex-1 flex-col overflow-hidden',
            blockingLoader && 'pointer-events-none invisible select-none'
          )}
          aria-hidden={blockingLoader}
        >
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-amber-100 flex-shrink-0">
          <div>
            <h2 id="choose-artwork-modal-title" className="text-xl md:text-2xl font-bold text-gray-800">
              {isCompare ? 'Choose an artwork to compare with' : 'Choose what you want to learn from today'}
            </h2>
            <p id="choose-artwork-modal-desc" className="text-sm text-gray-600 mt-1">
              {isCompare ? 'Pick one artwork to compare techniques and style.' : 'Pick one artwork to begin.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full text-gray-500 hover:bg-amber-50 hover:text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-400"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden p-4 md:p-6">
          {!loading && !error && (
            <div className="mb-3 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Showing {artworks.length} verified artworks
            </div>
          )}
          {error && !loading && (
            <div className="py-6 text-center text-amber-800 bg-amber-50 rounded-xl border border-amber-200">
              {error}
            </div>
          )}
          {!loading && !error && artworks.length > 0 && (
            <>
              <div
                ref={scrollRef}
                className={clsx(
                  'choose-artwork-scroll min-h-0 flex-1 overscroll-contain py-2',
                  pickerMobile
                    ? 'choose-artwork-scroll--mobile overflow-y-auto overflow-x-hidden'
                    : 'overflow-x-auto overflow-y-auto'
                )}
                role="list"
                aria-label="Artworks to choose from"
              >
                <div
                  className={clsx(
                    'min-h-0 min-w-0',
                    pickerMobile
                      ? 'flex w-full flex-col gap-4'
                      : 'flex flex-row items-start flex-shrink-0'
                  )}
                  style={pickerMobile ? undefined : { width: pickerStripLayout.stripW }}
                >
                  {!pickerMobile && pickerStripLayout.leadWidth > 0 && (
                    <div
                      aria-hidden
                      className="flex-shrink-0 pointer-events-none"
                      style={{ width: pickerStripLayout.leadWidth }}
                    />
                  )}
                  <div
                    className={clsx(
                      pickerMobile ? 'flex w-full flex-col gap-4' : 'flex flex-row gap-4 flex-shrink-0'
                    )}
                  >
                    {artworks.slice(pickerStripLayout.start, pickerStripLayout.end).map((art, sliceIdx) => {
                      const tileIndex = pickerStripLayout.start + sliceIdx;
                      const isSelected = selectedArtwork?.id === art.id;
                      const displayedType = normalizeArtworkType(art.typeOfArtwork || art.artworkType || '');
                      const displayedLevel = CARD_LEVEL_OPTIONS.includes(art.levelType) ? art.levelType : 'Unspecified';
                      const displayedMovement = String(art.artMovement || art.movement || '');
                      return (
                    <div
                      key={art.id}
                      data-picker-artwork-id={art.id}
                      className={clsx(
                        'choose-artwork-picker-card rounded-xl border-2 overflow-hidden text-left transition-all bg-white',
                        pickerMobile ? 'w-full max-w-full flex-shrink-0' : 'flex-shrink-0 w-[260px]'
                      )}
                      style={{
                        borderColor: isSelected ? 'rgb(245, 158, 11)' : 'rgba(255, 224, 130, 0.6)',
                        boxShadow: isSelected ? '0 0 0 3px rgba(255, 193, 7, 0.4)' : '0 4px 12px rgba(0,0,0,0.08)',
                      }}
                      role="listitem"
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedArtwork(art)}
                        className="w-full text-left focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2"
                        aria-pressed={isSelected}
                        aria-label={`Select ${art.title || 'Untitled'} by ${art.artistName || 'Unknown'}`}
                      >
                        <div className="relative w-full aspect-[4/3] overflow-hidden rounded-t-xl choose-artwork-tile-surface">
                          <ModalArtworkImage
                            previewUrl={art.pickerPreviewUrl || getArtworkPickerPreviewUrl(art.id)}
                            artworkId={art.id}
                            artworkTitle={art.title || art.artworkTitle || 'Untitled'}
                            artistName={art.artistName || art.artist || 'Unknown'}
                            fetchPriorityHigh={tileIndex < MODAL_HIGH_PRIORITY_TILES}
                          />
                          {isSelected && (
                            <div className="absolute inset-0 z-[5] bg-amber-400/20 flex items-center justify-center border-2 border-amber-500 rounded-xl pointer-events-none">
                              <span className="bg-amber-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                                Selected
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="p-3 border-t border-amber-100">
                          <div className="font-semibold text-gray-800 text-sm line-clamp-2">
                            {art.title || 'Untitled'}
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5">
                            {art.artistName || art.artist || 'Unknown'}
                          </div>
                          {art.locationCity && (
                            <div className="text-xs text-gray-500 mt-0.5">{art.locationCity}</div>
                          )}
                        </div>
                      </button>
                      <div className="p-2 pt-0 border-t border-amber-100/70 grid grid-cols-2 gap-2">
                        <select
                          value={displayedType}
                          onChange={(e) => updateArtworkMeta(art, e.target.value, displayedLevel, displayedMovement)}
                          className="px-2 py-1.5 rounded-lg border border-amber-200 bg-amber-50 text-[11px] text-amber-900"
                        >
                          <option value="">Uncategorized</option>
                          {!CARD_ARTWORK_TYPES.includes(displayedType) && displayedType && (
                            <option value={displayedType}>{displayedType}</option>
                          )}
                          {CARD_ARTWORK_TYPES.map((opt) => (
                            <option key={`${art.id}-type-${opt}`} value={opt}>{opt}</option>
                          ))}
                        </select>
                        <select
                          value={displayedLevel}
                          onChange={(e) => updateArtworkMeta(art, displayedType, e.target.value, displayedMovement)}
                          className="px-2 py-1.5 rounded-lg border border-sky-200 bg-sky-50 text-[11px] text-sky-900"
                        >
                          {CARD_LEVEL_OPTIONS.map((opt) => (
                            <option key={`${art.id}-level-${opt}`} value={opt}>{opt}</option>
                          ))}
                        </select>
                        <select
                          value={displayedMovement}
                          onChange={(e) => updateArtworkMeta(art, displayedType, displayedLevel, e.target.value)}
                          className="col-span-2 px-2 py-1.5 rounded-lg border border-violet-200 bg-violet-50 text-[11px] text-violet-900"
                        >
                          <option value="">Art movement</option>
                          {!CARD_ART_MOVEMENTS.includes(displayedMovement) && displayedMovement && (
                            <option value={displayedMovement}>{displayedMovement}</option>
                          )}
                          {CARD_ART_MOVEMENTS.map((opt) => (
                            <option key={`${art.id}-movement-${opt}`} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                      {(savingById[art.id] || saveErrById[art.id]) && (
                        <p className={`px-2 pb-2 text-[11px] ${saveErrById[art.id] ? 'text-red-600' : 'text-emerald-700'}`}>
                          {saveErrById[art.id] ? saveErrById[art.id] : 'Saving…'}
                        </p>
                      )}
                      <div className="px-2 pb-2 text-[10px] text-slate-400 font-mono truncate">
                        {art.id}
                      </div>
                      <div className="px-2 pb-2 flex justify-end">
                        <button
                          type="button"
                          onClick={() => void deleteArtwork(art)}
                          disabled={!!deletingById[art.id]}
                          className="text-[11px] font-semibold px-2.5 py-1 rounded-lg border border-red-200 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50"
                        >
                          {deletingById[art.id] ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    </div>
                      );
                    })}
                  </div>
                  {!pickerMobile && pickerStripLayout.trailing > 0 && (
                    <div
                      aria-hidden
                      className="flex-shrink-0 pointer-events-none"
                      style={{ width: pickerStripLayout.trailing }}
                    />
                  )}
                </div>
              </div>

              <div className="flex-shrink-0 pt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={!selectedArtwork}
                  className="py-2.5 px-6 rounded-xl text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: selectedArtwork
                      ? 'linear-gradient(180deg, #FFE082 0%, #FFD54F 100%)'
                      : 'rgba(255, 235, 180, 0.6)',
                    boxShadow: selectedArtwork ? '0 2px 8px rgba(255, 193, 7, 0.35)' : 'none',
                  }}
                >
                  {label}
                </button>
              </div>
            </>
          )}
        </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modalTree;
  return createPortal(modalTree, document.body);
};

const IS_PRODUCTION = process.env.NODE_ENV === 'production';

/**
 * Picker tile: only `/api/artwork-preview/{id}.webp` (no proxy / master URLs).
 */
function ModalArtworkImage({
  previewUrl = '',
  artworkId = '',
  artworkTitle = '',
  artistName = '',
  fetchPriorityHigh = false,
}) {
  const tileRef = useRef(null);
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  /** True only when URL missing or image fired `onError` — never for slow loads (no fake "Preview missing"). */
  const [showPlaceholder, setShowPlaceholder] = useState(false);

  const src = useMemo(() => String(previewUrl || '').trim(), [previewUrl]);

  useEffect(() => {
    setLoaded(false);
    setShowPlaceholder(!src);
  }, [artworkId, src]);

  // If the browser fulfilled from cache before React event wiring, mark as loaded.
  useEffect(() => {
    if (!src || loaded) return;
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0) {
      setLoaded(true);
      setShowPlaceholder(false);
    }
  }, [src, loaded]);

  const handleImgLoad = useCallback(
    (e) => {
      const el = e?.currentTarget;
      setLoaded(true);
      setShowPlaceholder(false);
      if (!DEBUG_ARTWORK_IMAGES || !el) return;
      // eslint-disable-next-line no-console
      console.log('[ArtworkPickerImage]', {
        artworkId,
        title: artworkTitle,
        previewUrl: src,
        naturalWidth: el.naturalWidth,
        naturalHeight: el.naturalHeight,
      });
    },
    [artworkId, artworkTitle, src]
  );

  const handleImgError = useCallback(() => {
    setLoaded(false);
    setShowPlaceholder(true);
  }, []);

  const showShimmer = Boolean(src && !loaded && !showPlaceholder);

  return (
    <div
      ref={tileRef}
      className="absolute inset-0 h-full w-full min-h-0 overflow-hidden choose-artwork-tile-surface"
    >
      {DEBUG_ARTWORK_IMAGES && (
        <div className="absolute left-1 top-1 z-[6] rounded bg-black/70 px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-tight text-white">
          {showPlaceholder ? 'MISSING' : 'PREVIEW'}
        </div>
      )}
      {showShimmer && (
        <div className="absolute inset-0 z-[3] overflow-hidden" aria-hidden>
          <div className="absolute inset-0 choose-artwork-tile-loading-base" />
          <div className="absolute inset-0 choose-artwork-tile-loading-shimmer" />
        </div>
      )}
      {showPlaceholder && !loaded && (
        <div className="absolute inset-0 z-[4] flex flex-col justify-center gap-1.5 px-4 text-center bg-gradient-to-br from-amber-50/95 to-stone-100/90">
          <p className="line-clamp-3 text-sm font-semibold leading-snug text-stone-800">{artworkTitle}</p>
          <p className="text-xs text-stone-600">{artistName}</p>
          {!IS_PRODUCTION && (
            <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-900/80">Preview missing</p>
          )}
        </div>
      )}
      {src ? (
        <img
          ref={imgRef}
          key={`${artworkId}-${src}`}
          src={src}
          alt=""
          sizes="260px"
          loading="eager"
          decoding="async"
          fetchpriority={fetchPriorityHigh ? 'high' : 'auto'}
          className="absolute inset-0 z-[2] block h-full w-full object-cover object-center aspect-[4/3] opacity-100"
          onLoad={handleImgLoad}
          onError={handleImgError}
        />
      ) : null}
    </div>
  );
}

export default ChooseArtworkModal;
